import React, { useState, useCallback, useEffect } from 'react';
import dayjs from 'dayjs';
import useStyles from '@front10/helpers/dist/UseStyles';
import { withQueryClient } from '@front10/container-elements';
import { ThemeProvider } from '@front10/base-elements';
import { EmotionCache } from '../_commons/emotionCache';

import withCommonEMContainer from '../_commons/CommonEMContainer/CommonEMContainer';
import { getTravelsRestrictions } from '../../../_integrations/everymundo/travels';
import { getAirports as getAirportsFn } from '../../../_integrations/everymundo/airports';
import { OriginDestinationLogic } from '../_commons/filters';
import useFilters from './hooks/filters';
import { openPopup } from '../../../_integrations/everymundo/tracking/index';
import { journeyTypeToLongValue } from '../../../helpers/utils';
import {
  MobileBody,
  MobileFilterDialog,
  MobileFooter,
  MobileHeader
} from './../../../components/lab/MobileFilterView';
import Loading from '../../others/Loading';
import FocusLock from 'react-focus-lock';

//-------------------------------------
// Basic components here
//-------------------------------------
import Button from '../../elements/Button';
import Header from '../../elements/Header';
import Box from '../../elements/Box';
import Icon from '../../elements/Icon';
import Text from '../../elements/Text';
import RequirementsAccordion from './atoms/RequirementsAccordion';
import RouteFilters from './atoms/RouteFilters';
import SecondaryFilters from './atoms/SecondaryFilters';
import ContainerErrorAlert from '../../alerts/ContainerErrorAlert';
import Disclaimer from '../../others/Disclaimer';
import RadioSelect from '../../elements/RadioSelect';

function getPubSub() {
  if (window && window.EM && window.EM.utils && window.EM.utils.PubSub) {
    return window.EM.utils.PubSub;
  }
  return undefined;
}
const flightTypeMap = {
  rt: 'ROUND_TRIP',
  ow: 'ONE_WAY',
  ROUND_TRIP: 'rt',
  ONE_WAY: 'ow'
};

const enforcementMapper = [
  'MANDATORY',
  'MAY_BE_REQUIRED',
  'NOT_REQUIRED',
  'RECOMMENDED',
  'NOT_REQUIRED_SMARVEL',
  'MAY_BE_REQUIRED_SMARVEL',
  'REQUIRED_SMARVEL',
  'NOT_INFO_SMARVEL',
  'ALLOWED_SMARVEL',
  'MAY_BE_ALLOWED_SMARVEL',
  'NOT_ALLOWED_SMARVEL'
];

const TravelRestrictionContainer = (props) => {
  const {
    defaultVisible,
    direction,
    translate,
    theme,
    headDisclaimer,
    alertContainerSettings,
    readOnlyInformationContainerSettings,
    labelTextReadOnlyResidencySettings,
    nationalityContainerSettings,
    labelTextReadOnlyNationalitySettings,
    infoTextReadOnlyNationalitySettings,
    residencyContainerSettings,
    infoTextReadOnlyResidencySettings,
    topInformationContainerSettings,
    accordionSettings = {
      iconsMapper: {
        NO_RESTRICTION: 'check-circle',
        NO_ENTRY: 'block',
        ENTRY: 'block',
        RESTRICTED_ENTRY: 'block',
        NO_EXIT: 'block',
        EXIT: 'block',
        RESTRICTED_EXIT: 'block',
        BOARDING: 'block',
        TRANSIT: 'block',
        QUARANTINE: 'home',
        DOC_REQUIRED: 'note-add',
        PPE: 'mask',
        HEALTH_ASSESSMENT: 'medical-services',
        COVID_19_TEST: 'coronavirus',
        NO_COVID_19_TEST: 'coronavirus',
        HEALTH_MEASURES: 'medical-services',
        SANITIZATION: 'sanitizer',
        DEPOSIT_REQUIRED: 'monetization',
        DEFAULT: 'note-add'
      }
    },
    tabViewContainerSettings = {},
    quickFactsBlockSettings = {},
    quickFactsContainerSettings = {},
    tabViewSettings = {},
    integrationSettings,
    prePopulation,
    showQuickFactsIcons = true,
    getTravelsRestrictions,
    titleSettings = {},
    travelRestrictionContainer = {},
    meta,
    pageContext,
    disclaimer = {},
    footerDisclaimer = {},
    travelModalDialog = {},
    travelModalContent = {
      padding: '4'
    },
    travelModalHeader = {},
    travelModalBody = {},
    travelModalFooter = {
      display: 'flex',
      justifyContent: 'end'
    },
    continueBooking = {},
    continueBookingLabelKey = 'cmp-travel-restriction-continue-booking',
    iconClose = {},
    buttonClose = {},
    alertTravelInfo = {},
    alertTravelInfoError = {},
    alertTravelInfoIcon = {},
    alertTravelInfoMsg = {},
    alertInfoTextSettings = {},
    alertTravelInfoTitle = {},
    alertError = {},
    footerImg = [
      prePopulation.travelRestrictionsSource &&
      prePopulation.travelRestrictionsSource.toLowerCase() === 'smartvel'
        ? {
            url:
              'https://res.cloudinary.com/dakp804eh/image/upload/v1641480712/airmodules/Utils/image.png',
            alt: 'smartvel'
          }
        : {
            url:
              'https://res.cloudinary.com/dakp804eh/image/upload/v1632327677/airmodules/Utils/poweredbysherpa-onlight.png',
            alt: 'sherpa'
          }
    ],
    poweredByContainer,
    poweredBy,
    showInfoAlert = true,
    filters,
    filtersTravelRestriction,
    initialOrigins = [],
    initialDestinations = [],
    originDestinationLogic = OriginDestinationLogic.DIFFERENT,
    getAirports = getAirportsFn,
    isMobile = false,
    bodySettings,
    loading = {},
    noRestrictionsAvailableContainerSettings = {},
    labelTextNoRestrictionsAvailableSettings = {},
    scrolledContentSettings = {}
  } = props;

  const [className, style] = useStyles(travelRestrictionContainer, { direction, theme });
  const { designTokens } = theme;
  const [isVisible, setVisible] = useState(defaultVisible ?? false);
  const [isCleanSomeFilter, setIsCleanSomeFilter] = useState(defaultVisible ?? false);
  const [firstLoading, setFirstLoading] = useState(false);
  const [popupData, setPopupData] = useState({});
  const closeButtonRef = React.useRef(null);
  const [closeButtonFocus, setCloseButtonFocus] = useState(false);
  const [isChangeSecondaryFilter, setIsChangeSecondaryFilter] = useState(false);

  //INFO: This fix is necessary for change layout when user interact with flightType filter
  const newFiltersTravelRestriction = React.useMemo(() => {
    return {
      ...filtersTravelRestriction,
      originDestinationSettings: {
        ...filtersTravelRestriction.originDestinationSettings,
        components: {
          ...filtersTravelRestriction.originDestinationSettings.components,
          column: {
            ...filtersTravelRestriction.originDestinationSettings.components.column,
            flex: isMobile ? null : popupData?.journeyType === 'rt' ? '1' : '2'
          }
        }
      }
    };
  }, [filtersTravelRestriction, isMobile, popupData?.journeyType]);

  const open = useCallback(() => {
    setVisible(true);
    setFirstLoading(true);
    setIsChangeSecondaryFilter(false);
    setTimeout(() => closeButtonRef.current?.focus(), 500);
  }, []);
  const close = useCallback(() => {
    setVisible(false);
    setFirstLoading(false);
    //   track(VerticalBookingActions.POPUP_CLOSE, currentPopupData);
  }, []);

  const {
    filtersState,
    filtersState: { locations },
    dispatch,
    match,
    travelsInfo,
    retry,
    originCountry
  } = useFilters({
    filters,
    filtersTravelRestriction: newFiltersTravelRestriction,
    initialOrigins,
    initialDestinations,
    originDestinationLogic,
    prePopulation,
    popupData,
    integrationSettings,
    meta,
    pageContext,
    getAirports,
    isVisible,
    setFirstLoading,
    firstLoading,
    getTravelsRestrictions,
    availableFilters: newFiltersTravelRestriction.secondaryFilters.availableFilters
  });

  const isLoading = match('LOADING');
  const optionsTabs = React.useMemo(() => {
    const options = [
      {
        value: 'departure-restriction',
        name: translate('cmp-travel-restriction-departure-restriction', 'Departure')
      }
    ];
    if (filtersState.flightType.value === 'rt' || filtersState.flightType.value === 'ROUND_TRIP') {
      options.push({
        value: 'return-restriction',
        name: translate('cmp-travel-restriction-return-restriction', 'Return')
      });
    }
    return options;
  }, [translate, filtersState.flightType.value]);

  const [tabActive, setTabActive] = useState(optionsTabs[0]);

  const currentFilters = React.useMemo(() => {
    let filter = 'DatePicker';
    if (popupData.journeyType === 'rt' || popupData.journeyType === 'ROUND_TRIP') {
      filter = 'DateRangeSelector';
    }
    return ['OriginDestination'].concat(filter).concat('CallToAction');
  }, [popupData.journeyType]);

  const updatefilterState = () => {
    if (popupData.journeyType === 'rt') {
      const validation =
        filtersState.locations.destination.value === undefined ||
        filtersState.locations.origin.value === undefined ||
        filtersState.dateRange.value.start === undefined ||
        filtersState.dateRange.value.start === '' ||
        filtersState.dateRange.value.end === undefined ||
        filtersState.dateRange.value.end === '';
      setIsCleanSomeFilter(validation);
    } else {
      const validation =
        filtersState.locations.destination.value === undefined ||
        filtersState.locations.origin.value === undefined ||
        filtersState.dateRange.value.start === undefined ||
        filtersState.dateRange.value.start === '';
      setIsCleanSomeFilter(validation);
    }
    setTabActive(optionsTabs[0]);
    filtersState.setIsFetchData(false);
  };

  useEffect(() => {
    if (filtersState.flightType !== undefined) {
      if (filtersState.dateRange.value.start) {
        if (
          filtersState.flightType.value === 'ROUND_TRIP' ||
          filtersState.flightType.value === 'rt'
        ) {
          dispatch({
            type: 'DATE_RANGE_CHANGE',
            payload: {
              start: filtersState.dateRange.value.start,
              end: filtersState.dateRange?.value?.end ?? filtersState.dateRange.value.start
            }
          });
        }
      }

      let newJourneyType = filtersState.flightType.value;
      let newFlightType = filtersState.flightType.value;
      if (
        filtersState.flightType.value === 'ONE_WAY' ||
        filtersState.flightType.value === 'ROUND_TRIP'
      ) {
        newJourneyType = flightTypeMap[filtersState.flightType.value];
      }
      if (filtersState.flightType.value === 'ow' || filtersState.flightType.value === 'rt') {
        newFlightType = flightTypeMap[filtersState.flightType.value];
      }
      setPopupData({
        ...popupData,
        journeyType: newJourneyType,
        flightType: newFlightType,
        origin: filtersState.locations.origin.value
      });
    }
    setIsChangeSecondaryFilter(true);
    updatefilterState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersState.flightType]);

  useEffect(() => {
    setIsChangeSecondaryFilter(true);
    updatefilterState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersState.fullyVaccinated]);
  useEffect(() => {
    if (filtersState.locations.destination.value !== undefined) {
      setPopupData({
        ...popupData,
        destination: filtersState.locations.destination.value,
        origin: filtersState.locations.origin.value
      });
    }
    updatefilterState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersState.locations.destination.value]);
  useEffect(() => {
    if (filtersState.locations.origin.value !== undefined) {
      setPopupData({ ...popupData, origin: filtersState.locations.origin.value, originCountry });
    }
    updatefilterState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersState.locations.origin.value]);
  useEffect(() => {
    if (filtersState.dateRange.value.end !== undefined) {
      setPopupData({
        ...popupData,
        dateRange: {
          start: filtersState.dateRange.value.start,
          end: filtersState.dateRange.value.end
        },
        origin: filtersState.locations.origin.value
      });
    }
    updatefilterState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersState.dateRange.value.end]);
  useEffect(() => {
    if (filtersState.dateRange.value.start !== undefined) {
      setPopupData({
        ...popupData,
        dateRange: {
          start: filtersState.dateRange.value.start,
          end: filtersState.dateRange.value.end
        },
        origin: filtersState.locations.origin.value
      });
    }
    updatefilterState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersState.dateRange.value.start]);

  useEffect(() => {
    const pubSub = getPubSub?.();
    let token = pubSub?.subscribe('em.travel.restriction.open', (_event, data) => {
      const { departureDate, returnDate, journeyType } = data;
      let newReturnDate;
      if (returnDate === '') {
        newReturnDate = returnDate;
      } else {
        if (returnDate) {
          newReturnDate = dayjs(returnDate).toDate();
        } else {
          if (departureDate === '') {
            newReturnDate = departureDate;
          } else {
            newReturnDate = dayjs(departureDate).toDate();
          }
        }
      }
      const newData = {
        ...data,
        origin: prePopulation.origin || data.origin,
        destination: prePopulation.destination || data.destination,
        dateRange: {
          start: departureDate === '' ? departureDate : dayjs(departureDate).toDate(),
          end: newReturnDate
        },
        flightType: journeyTypeToLongValue()[journeyType],
        fullyVaccinated: prePopulation.fullyVaccinated
      };
      setPopupData(newData);
      locations.origin.retry();
      dispatch({
        type: 'SET_ALL',
        payload: {
          origin: { value: data.origin },
          destination: { value: data.destination },
          dateRange: newData.dateRange,
          flightType: newData.journeyType,
          fullyVaccinated: newData.fullyVaccinated
        }
      });
      open();
    });
    return () => {
      pubSub?.unsubscribe(token);
    };
  }, [dispatch, locations.origin, open, prePopulation]);

  if (!isVisible) {
    return null;
  }

  const isVAlidEnforcement = (enforcement) => {
    return enforcementMapper.findIndex((e) => e === enforcement) !== -1;
  };

  const isShow = (category, list) => {
    return list.findIndex((e) => e === category) !== -1;
  };

  const getIcons = (typeRestriction) => {
    let data = [];
    if (typeRestriction === 'departure-restriction') {
      data = travelsInfo.arrive;
    } else {
      data = travelsInfo.returning;
    }
    let listQuickFacts = [];
    let renderItem = null;
    let count = 0;
    return (
      <>
        {data.map(
          (item) =>
            (item.type === 'DESTINATION_TRAVEL_RESTRICTIONS' ||
              item.type === 'DESTINATION_DOCUMENTS' ||
              (item.type === 'LOCAL_RESTRICTIONS' &&
                prePopulation.travelRestrictionsSource &&
                prePopulation.travelRestrictionsSource.toLowerCase() === 'smartvel')) &&
            item.data.map((i) => {
              renderItem = null;
              if (
                i.enforcement &&
                isVAlidEnforcement(i.enforcement) &&
                !isShow(i.category, listQuickFacts)
              ) {
                if (count++ <= 4) {
                  listQuickFacts.push(i.category);
                  renderItem = (
                    <Box
                      display="flex"
                      alignItems="center"
                      {...quickFactsBlockSettings}
                      className="quickFactsBlockSettings"
                    >
                      <Icon
                        {...alertTravelInfoIcon}
                        icon={accordionSettings.iconsMapper[i.category]}
                      />
                      <Text>
                        {translate(
                          `cmp-travel-restriction-quick-fact-${i.category.replaceAll('_', '-')}`
                            .toString()
                            .toLowerCase(),
                          `cmp-travel-restriction-quick-fact-${i.category.replaceAll('_', '-')}`
                            .toString()
                            .toLowerCase()
                        )}
                      </Text>
                    </Box>
                  );
                  return renderItem;
                } else {
                  return null;
                }
              }
              return null;
            })
        )}
      </>
    );
  };

  const notRestrictions = () => {
    if (travelsInfo === null) {
      return false;
    }
    if (
      prePopulation.travelRestrictionsSource &&
      prePopulation.travelRestrictionsSource.toLowerCase() === 'sherpa'
    ) {
      for (let index = 0; index < travelsInfo.arrive.length; index++) {
        const element = travelsInfo.arrive[index];
        if (element.data.length !== 0) {
          return false;
        }
      }
      if (popupData.journeyType === 'rt') {
        for (let index = 0; index < travelsInfo.returning.length; index++) {
          const element = travelsInfo.returning[index];
          if (element.data.length !== 0) {
            return false;
          }
        }
        return true;
      } else {
        return true;
      }
    } else if (
      prePopulation.travelRestrictionsSource &&
      prePopulation.travelRestrictionsSource.toLowerCase() === 'smartvel'
    ) {
      if (popupData.journeyType === 'rt' || popupData.journeyType === 'ROUND_TRIP') {
        if (travelsInfo.arrive.length === 0 && travelsInfo.returning.length === 0) {
          return true;
        }
      } else {
        if (travelsInfo.arrive.length === 0) {
          return true;
        }
      }
      return false;
    }
  };

  const notExistRestricted = notRestrictions();
  const getSmartvelLabelInfo = () => {
    if (popupData.journeyType === 'ROUND_TRIP' || popupData.journeyType === 'rt') {
      if (
        travelsInfo &&
        travelsInfo.arrive.length === 0 &&
        travelsInfo.returning.length !== 0 &&
        tabActive.value === 'departure-restriction'
      ) {
        return (
          <Box {...noRestrictionsAvailableContainerSettings}>
            <Text {...labelTextNoRestrictionsAvailableSettings}>
              {translate(
                'cmp-travel-restriction-no-restrictions-available-departure',
                'Departure restrictions are not available'
              )}
            </Text>
          </Box>
        );
      } else if (
        travelsInfo &&
        travelsInfo.returning.length === 0 &&
        travelsInfo.arrive.length !== 0 &&
        tabActive.value !== 'departure-restriction'
      ) {
        return (
          <Box {...noRestrictionsAvailableContainerSettings}>
            <Text {...labelTextNoRestrictionsAvailableSettings}>
              {translate(
                'cmp-travel-restriction-no-restrictions-available-return',
                'Return restrictions are not available'
              )}
            </Text>
          </Box>
        );
      }
    }
    return null;
  };

  const labelContent =
    prePopulation?.travelRestrictionsSource &&
    prePopulation?.travelRestrictionsSource.toLowerCase() === 'smartvel'
      ? getSmartvelLabelInfo()
      : null;
  const tmpObject = isMobile ? {} : { height: '100%' };
  const newScrolledContentSettings =
    labelContent !== null
      ? {
          ...scrolledContentSettings,
          'cmp-styles': {
            ...scrolledContentSettings['cmp-styles'],
            ...tmpObject
          }
        }
      : scrolledContentSettings;

  const scrolledContent = (
    <Box {...newScrolledContentSettings} ariaHidden={closeButtonFocus}>
      {isLoading && <Loading {...loading} isLoading={false} />}
      {match('ERROR') && !isLoading && <ContainerErrorAlert {...alertError} tryAgain={retry} />}
      {match('COMPLETE') &&
        showInfoAlert &&
        // travelsInfo.country.summary &&
        !notExistRestricted &&
        filtersState.isfetchData && (
          <Box
            {...alertTravelInfo}
            display="flex"
            flexDirection="column"
            className={`fr-alert-type-${tabActive.value}`}
          >
            {prePopulation.travelRestrictionsSource &&
            prePopulation.travelRestrictionsSource.toLowerCase() === 'smartvel' ? (
              <Box display="flex" flexDirection="row" {...alertContainerSettings}>
                <Text {...alertTravelInfoTitle}>
                  {translate('cmp-travel-restriction-alert-title', 'Need to Know')}
                </Text>
                <Disclaimer
                  labelKey="cmp-travel-restriction-quick-facts-disclaimer"
                  {...headDisclaimer}
                />
              </Box>
            ) : (
              <Text {...alertTravelInfoTitle}>
                {translate('cmp-travel-restriction-alert-title', 'Need to Know')}
              </Text>
            )}
            <Text {...alertTravelInfoMsg}>
              {tabActive.value === 'departure-restriction'
                ? travelsInfo.summaryDeparture.msg
                : travelsInfo.summaryReturn.msg}
            </Text>
            {showQuickFactsIcons && (
              <Box
                display="flex"
                className={`fr-quick-facts-container`}
                {...quickFactsContainerSettings}
              >
                {getIcons('departure-restriction')}
              </Box>
            )}
          </Box>
        )}
      {filtersState.isfetchData && !notExistRestricted && (
        <>
          <Disclaimer {...disclaimer} />
          <Box className="tab-view" {...tabViewContainerSettings}>
            <RadioSelect
              {...tabViewSettings}
              selectedOption={tabActive}
              options={optionsTabs}
              onChange={(e) => {
                setTabActive(e);
              }}
            />
          </Box>
        </>
      )}
      {filtersState.isfetchData && (
        <Box className="fr-modal-body" {...travelModalBody}>
          {match('COMPLETE') && (
            <>
              {labelContent}
              <RequirementsAccordion
                data={travelsInfo}
                {...accordionSettings}
                activeTab={tabActive.value}
              />
            </>
          )}
        </Box>
      )}
    </Box>
  );

  const secondaryFilters = (
    <SecondaryFilters
      showClearButton={false}
      {...filters}
      {...filtersState}
      {...newFiltersTravelRestriction.secondaryFilters}
      availableFilters={filtersState.availableSecondaryFilters}
      filter={{ ...{ prePopulation, integrationSettings, meta, getAirports } }}
    />
  );
  const body = (
    <Box {...bodySettings}>
      {!isMobile && (
        <div aria-hidden={closeButtonFocus}>
          {prePopulation.travelRestrictionsSource &&
          prePopulation.travelRestrictionsSource.toLowerCase() === 'smartvel' ? (
            <Box display="flex" flexDirection="row" {...topInformationContainerSettings}>
              <> {secondaryFilters}</>
              <Box display="flex" flexDirection="row" {...readOnlyInformationContainerSettings}>
                <Box {...nationalityContainerSettings}>
                  <Text {...labelTextReadOnlyNationalitySettings}>
                    {translate('cmp-travel-restriction-nationality-label', 'Nationality:')}
                  </Text>
                  <Text {...infoTextReadOnlyNationalitySettings}>
                    {filtersState?.locations?.origin?.value &&
                    filtersState?.locations?.origin?.value !== ''
                      ? popupData.dataType === 'EMPTY_FROM_API'
                        ? originCountry
                        : popupData?.originCountry
                      : translate('cmp-travel-restriction-empty-value', '-')}
                  </Text>
                </Box>
                <Box {...residencyContainerSettings}>
                  <Text {...labelTextReadOnlyResidencySettings}>
                    {translate('cmp-travel-restriction-residency-label', 'Residency:')}
                  </Text>
                  <Text {...infoTextReadOnlyResidencySettings}>
                    {filtersState?.locations?.origin?.value &&
                    filtersState?.locations?.origin?.value !== ''
                      ? popupData.dataType === 'EMPTY_FROM_API'
                        ? originCountry
                        : popupData?.originCountry
                      : translate('cmp-travel-restriction-empty-value', '-')}
                  </Text>
                </Box>
              </Box>
            </Box>
          ) : (
            <> {secondaryFilters}</>
          )}
        </div>
      )}
      {!isMobile && (
        <RouteFilters
          {...filters}
          {...newFiltersTravelRestriction}
          {...filtersState}
          callToActionSettings={{
            ...filtersState.callToActionSettings,
            disabled: isCleanSomeFilter,
            onClick: (e) => {
              filtersState?.callToActionSettings?.onClick();
              setIsChangeSecondaryFilter(false);
            }
          }}
          availableFilters={currentFilters}
          setCloseButtonFocus={setCloseButtonFocus}
          closeButtonFocus={closeButtonFocus}
        />
      )}
      {isMobile && (
        <RouteFilters
          {...filters}
          {...newFiltersTravelRestriction}
          {...filtersState}
          isMobile={true}
          callToActionSettings={{
            ...filtersState.callToActionSettings,
            disabled: isCleanSomeFilter,
            onClick: (e) => {
              filtersState?.callToActionSettings?.onClick();
              setIsChangeSecondaryFilter(false);
            }
          }}
          availableFilters={currentFilters}
          secondaryFilter={
            <SecondaryFilters
              showClearButton={false}
              {...filters}
              {...filtersState}
              {...filtersTravelRestriction.secondaryFilters}
              availableFilters={filtersState.availableSecondaryFilters}
              filter={{ ...{ prePopulation, integrationSettings, meta, getAirports } }}
            />
          }
          complementaryInformation={
            prePopulation.travelRestrictionsSource &&
            prePopulation.travelRestrictionsSource.toLowerCase() === 'smartvel' ? (
              <Box display="flex" flexDirection="column" {...readOnlyInformationContainerSettings}>
                <Box {...nationalityContainerSettings}>
                  <Text {...labelTextReadOnlyNationalitySettings}>
                    {translate('cmp-travel-restriction-nationality-label', 'Nationality:')}
                  </Text>
                  <Text {...infoTextReadOnlyNationalitySettings}>
                    {filtersState?.locations?.origin?.value &&
                    filtersState?.locations?.origin?.value !== ''
                      ? popupData.dataType === 'EMPTY_FROM_API'
                        ? originCountry
                        : popupData?.originCountry
                      : translate('cmp-travel-restriction-empty-value', '-')}
                  </Text>
                </Box>
                <Box {...residencyContainerSettings}>
                  <Text {...labelTextReadOnlyResidencySettings}>
                    {translate('cmp-travel-restriction-residency-label', 'Residency:')}
                  </Text>
                  <Text {...infoTextReadOnlyResidencySettings}>
                    {filtersState?.locations?.origin?.value &&
                    filtersState?.locations?.origin?.value !== ''
                      ? popupData.dataType === 'EMPTY_FROM_API'
                        ? originCountry
                        : popupData?.originCountry
                      : translate('cmp-travel-restriction-empty-value', '-')}
                  </Text>
                </Box>
              </Box>
            ) : null
          }
        />
      )}
      {!match('ERROR') &&
        ((popupData.dataType === 'EMPTY_FROM_API' && !filtersState.isfetchData) ||
          (!isLoading && (!filtersState.isfetchData || notExistRestricted))) && (
          <Box {...alertTravelInfoError} className={'fr-alert-info'} ariaHidden={closeButtonFocus}>
            <Text
              {...alertInfoTextSettings}
              dangerouslySetInnerHTML={{
                __html:
                  isChangeSecondaryFilter === true
                    ? translate(
                        'cmp-travel-restriction-no-route-selected',
                        'Please fill in missing fields to see the requirements and restrictions for your destination. Update to see new results. '
                      )
                    : notExistRestricted
                    ? translate(
                        'cmp-travel-restriction-no-restrictions-available',
                        "You will not be required to quarantine or provide a COVID-19 test, but other requirements may exist. Please visit our <a href='https://www.singaporeair.com/en_UK/sg/travel-info/covid-19/' target='_blank'>COVID-19 Information Center</a> for details."
                      )
                    : translate(
                        'cmp-travel-restriction-no-route-selected',
                        'Please fill in missing fields to see the requirements and restrictions for your destination. Update to see new results. '
                      )
              }}
            ></Text>
          </Box>
        )}

      {scrolledContent}
    </Box>
  );
  const footer = (
    <>
      <Box className="fr-modal-footer" {...travelModalFooter} ariaHidden={closeButtonFocus}>
        <Box {...poweredByContainer}>
          <Box display="flex" alignItems="center" {...poweredBy}>
            <Disclaimer labelKey="cmp-travel-restriction-footer-disclaimer" {...footerDisclaimer} />
            {footerImg.map(({ url, alt }) => (
              <img className={alt} alt={alt} src={url} />
            ))}
          </Box>
        </Box>
        <Button
          ariaLabel={translate(continueBookingLabelKey, 'Continue Booking')}
          {...continueBooking}
          onClick={() => {
            close();
            let newPopupData = { ...popupData, departureDate: popupData.dateRange.start };
            if (popupData.flightType === 'ROUND_TRIP') {
              newPopupData = { ...newPopupData, returnDate: popupData.dateRange.end };
            } else {
              if (newPopupData.returnDate) {
                delete newPopupData['returnDate'];
              }
            }
            openPopup(newPopupData);
          }}
          disabled={!filtersState.isfetchData}
        >
          {translate(continueBookingLabelKey, 'Continue booking')}
        </Button>
      </Box>
    </>
  );

  return (
    <>
      <FocusLock autoFocus returnFocus>
        {!isMobile && isVisible ? (
          <Box className={`${className} ${TravelRestrictionContainer.displayName}`} style={style}>
            <Box className="fr-modal-dialog" {...travelModalDialog}>
              <Box className="fr-modal-content" {...travelModalContent}>
                <Box
                  className="fr-modal-header"
                  {...travelModalHeader}
                  ariaHidden={closeButtonFocus}
                >
                  <Header
                    tag="h2"
                    dataTestId="headerTitle"
                    content={translate('cmp-travel-restriction-header-title', 'Travel Restriction')}
                    {...titleSettings}
                  />
                  <Button
                    ariaLabel="Close"
                    {...buttonClose}
                    onClick={() => close()}
                    focusReferences={(e) => (closeButtonRef.current = e)}
                  >
                    <Icon icon="close" {...iconClose} />
                  </Button>
                </Box>
                {body}
              </Box>
              {footer}
            </Box>
          </Box>
        ) : (
          isVisible && (
            <EmotionCache direction={direction}>
              <ThemeProvider theme={designTokens}>
                <MobileFilterDialog
                  label={props.label}
                  // onDismiss={
                  // dialog.dismiss
                  // }
                >
                  <MobileHeader
                    title={translate('cmp-travel-restriction-header-title', 'Travel Restriction')}
                    onBackButtonPress={() => {
                      close();
                      // if (props.onBackButtonPress) props.onBackButtonPress();
                      // dialog.dismiss();
                    }}
                  />
                  <MobileBody>{body}</MobileBody>
                  <MobileFooter align="end">{footer}</MobileFooter>
                </MobileFilterDialog>
              </ThemeProvider>
            </EmotionCache>
          )
        )}
      </FocusLock>
    </>
  );
};

TravelRestrictionContainer.defaultProps = {
  getTravelsRestrictions: getTravelsRestrictions,
  prePopulation: {
    /*INFO: queryFields is an array of parameters that will be
          sent to the API, it can be a string and that will 
          look for its value in the prePopulation or an object
          to define an alias when it is sent to the API */
    queryFields: [
      {
        languageCode: 'language'
      },
      {
        include: 'include'
      }
    ],
    languageCode: 'en'
  },
  meta: {
    name: 'travel-restriction',
    moduleId: 'em-cmp-lib-airmodules',
    version: '2.26.41',
    staticAssetsHost: 'https://em-frontend-assets.airtrfx.com'
  }
};

TravelRestrictionContainer.displayName = 'TravelRestrictionContainer';

export default withCommonEMContainer(withQueryClient(TravelRestrictionContainer));
